import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/** What a page puts into the shell's top bar. */
export interface ShellChrome {
    /** Small label above/next to the title, e.g. "Collab board". */
    kicker?: string;
    /** The CoSparks Workspace the space is in: named beside the title, linking to its page. */
    workspace?: { slug: string; name: string } | null;
    title?: ReactNode;
    /** Right side of the top bar, before the account menu (presence, share button...). */
    actions?: ReactNode;
    /** A notice floating just under the top bar, e.g. where the facilitator sent everyone next. */
    notice?: ReactNode;
}

type Setter = (chrome: ShellChrome) => void;

const ChromeContext = createContext<{ chrome: ShellChrome; setChrome: Setter }>({
    chrome: {},
    setChrome: () => { },
});

export function ShellChromeProvider({ children }: { children: ReactNode }) {
    const [chrome, setChrome] = useState<ShellChrome>({});
    return <ChromeContext.Provider value={{ chrome, setChrome }}>{children}</ChromeContext.Provider>;
}

export function useShellChromeValue() {
    return useContext(ChromeContext).chrome;
}

/**
 * Set the top bar's title and actions for the current page. Pass a stable deps list for the parts
 * that change (title text, presence count...). Cleared when the page unmounts.
 */
export function useShellChrome(chrome: ShellChrome, deps: unknown[]) {
    const { setChrome } = useContext(ChromeContext);
    useEffect(() => {
        setChrome(chrome);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    useEffect(() => () => setChrome({}), [setChrome]);
}
